/* eslint-disable no-unused-vars */
import { isNullOrUndefined } from 'util';
import { isEmpty } from 'lodash';
import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { Button } from 'antd';
import QuestionCircleOutlined from '@ant-design/icons/lib/icons/QuestionCircleOutlined';

import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import { joinClasses } from 'utils/stylesUtil';
import {
  findFrequencyByBenefitKind,
  getAdditionalServices,
  getConfiguredBenefitCodeList,
  getNotApplicableTitles,
  hideHeader,
  isInvalidDisplayValue,
  showRxRow,
  showRxRowTitle,
} from 'modules/benefitsWellness/util';
import AddOrRemovePlans from 'modules/benefitsWellness/components/AddOrRemovePlans/AddOrRemovePlans';
import useBenguide from 'hooks/benguide';
import MDVPlan from 'model/MDVPlan';
import meta from 'modules/benefitsWellness/data/metadataNew';
import MDVTypes from 'modules/benefitsWellness/enums/MDVTypes';
import MDVMetaGroup from 'model/MDVMetaGroup';
import MDVMetaService from 'model/MDVMetaService';
import TooltipPopover from 'components/TooltipPopover/TooltipPopover';
import PlanyearPopover from 'components/PlanyearPopover/PlanyearPopover';
import CustomRowModal from 'components/CustomRowModal/CustomRowModal';
import FrequencyConfiguration from 'modules/benefitsWellness/components/FrequencyConfiguration/FrequencyConfiguration';
import { TableContext } from 'modules/benefitsWellness/components/TableContext/TableContextProvider';
import iconEdit from 'images/icon-edit.svg';
import {
  DENTAL_PLAN_CHANNEL,
  MEDICAL_PLAN_CHANNEL,
  VISION_PLAN_CHANNEL,
} from 'modules/clients/UPClient/UPClientConst';
import { TOOLTIP_CONST } from 'constants/commonConstants';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { getText } from 'utils/stringUtil';
import {
  COST,
  FIELD_DESCRIPTION_KEYS,
  FIELD_KEYS,
  FREQUENCY_KEYS,
  HEADER_KEYS,
  RX_TIER_FIELD_DESCRIPTION_KEYS,
  RX_TIER_FIELD_KEYS,
  SPECIALIZED_FIELD_DESCRIPTION_KEYS,
  SPECIALIZED_FIELD_KEYS,
  YOUR_COST,
} from 'constants/languageConstants';
import OverflowPopover from 'components/PlanYearOverFlowPopover/OverflowPopover';
import { MDVCardValuesRef } from 'modules/benefitsWellness/components/MDVCardValues/MDVCardValues';
import { fieldMapping } from 'modules/benefitsWellness/constants/labelConstants';
import SyncedLabel from './SyncedLabel';
import styles from './mdvCardTitles.module.less';

type MDVCardTitlesProps = {
  planType: MDVTypes;
  onUpdatePlanList: Function;
  addedPlanList: MDVPlan[];
  reloadPlans: Function;
  allPlans: MDVPlan[];
  allowHeaderTitle?: boolean;
  showAlert: Function;
  isLoading: boolean;
  detailsHeight?: number;
  planColumns?: number[];
  isContributionsViewOpen?: boolean;
  onCloseContributions?: Function;
  cardValuesRefs?: React.RefObject<MDVCardValuesRef>[];
};

export const MDVCardTitles: FC<MDVCardTitlesProps> = (
  props: MDVCardTitlesProps
) => {
  const {
    planType,
    onUpdatePlanList,
    addedPlanList,
    reloadPlans,
    allPlans,
    allowHeaderTitle = false,
    showAlert,
    isLoading,
    detailsHeight,
    isContributionsViewOpen = false,
    planColumns,
    onCloseContributions = () => {},
    cardValuesRefs = [],
  } = props;
  const currentBenguide = useBenguide();
  const dispatch = useAppDispatch();
  const { getMaxHeight } = useContext(TableContext);
  const { rateValidations, selectedNetwork, rateValidationInProgress } =
    useAppSelector((state) => state.benefitWellness);
  const language = useAppSelector((state) => state.benguide.data?.language);
  const isEditMode = currentBenguide.isEditMode;

  const [dataGroups, setDataGroups] = useState<MDVMetaGroup[]>([]);
  const [additionalServices, setAdditionalServices] = useState<
    MDVMetaService[]
  >([]);
  const [contributionModalClose, setContributionModalClose] =
    useState<boolean>(false);
  const [notApplicableTitles, setNotApplicableTitles] = useState<string[]>([]);

  useEffect(() => {
    if (planType) {
      const fieldGroups = meta.benguideMetaData.planFieldGroupings.find(
        (planFieldGroup) => planFieldGroup.planType === planType.toLowerCase()
      );

      const definedBenefitCodes = getConfiguredBenefitCodeList(
        fieldGroups?.hotspots || []
      );
      setAdditionalServices(
        getAdditionalServices(
          addedPlanList,
          definedBenefitCodes,
          selectedNetwork
        )
      );
      setDataGroups(fieldGroups?.hotspots || []);
    }
  }, [
    planType,
    addedPlanList,
    dispatch,
    currentBenguide.masterId,
    selectedNetwork,
  ]);

  useEffect(() => {
    onCloseContributions(contributionModalClose);
  }, [contributionModalClose, onCloseContributions]);

  const computedTitles = useMemo(() => {
    if (isEditMode) return [];

    return getNotApplicableTitles(
      addedPlanList,
      planType,
      selectedNetwork === 'IN_NETWORK' ? 'IN' : 'OUT'
    );
  }, [isEditMode, addedPlanList, planType, selectedNetwork]);

  useEffect(() => {
    setNotApplicableTitles(computedTitles);
  }, [computedTitles]);

  const getFrequencyEditor = (isModelOpen: boolean, closeModal: any) => {
    return (
      <FrequencyConfiguration
        isOpenModal={isModelOpen || isContributionsViewOpen}
        closeModal={closeModal}
        benefitKind={planType.toUpperCase()}
        onCloseContributions={setContributionModalClose}
      />
    );
  };

  const getCustomRowEditor = (isModelOpen: boolean, closeModal: any) => {
    return (
      <CustomRowModal
        isOpenModal={isModelOpen}
        closeModal={closeModal}
        benguideId={currentBenguide.masterId}
        planList={addedPlanList}
        planType={planType}
      />
    );
  };

  const getMDVChannel = () => {
    if (planType === MDVTypes.MEDICAL) {
      return MEDICAL_PLAN_CHANNEL;
    } else if (planType === MDVTypes.DENTAL) {
      return DENTAL_PLAN_CHANNEL;
    } else {
      return VISION_PLAN_CHANNEL;
    }
  };

  const getMaxHeightWithFixedLabel = (title: string) => {
    let fixedTitle = title;
    if (fixedTitle.includes('_')) {
      fixedTitle = fixedTitle.substring(
        fixedTitle.lastIndexOf('_') + 1,
        fixedTitle.length
      );
    }

    if (title.includes('MEDICAL_RX_') && !title.includes('Tier')) {
      fixedTitle = 'Rx ' + fixedTitle;
    }

    return getMaxHeight(fixedTitle);
  };

  const nonExistCustomServices = {} as any;
  const nonExistRxCosts = {} as any;
  const nonExistDeductibleIndividual = {} as any;
  const showIfExistServiceList = dataGroups
    // in meta data file, the isCustomizedServices is should only be set true for services.
    .filter((titleGroup) => !!titleGroup.isCustomizedServices)
    .map((titleGroup) =>
      titleGroup.elements.filter((title: any) => title.showTitleIfExists)
    )[0] as MDVMetaService[];

  const showIfExistRxCostsList = dataGroups
    ?.filter((item) => item?.isCustomizedRX)
    ?.flatMap((item) => item?.elements) as MDVMetaService[];

  const showIfExistDeductibleIndividualList = dataGroups
    ?.filter((item) => item?.isCustomizedDeductible)
    ?.flatMap((item) => item?.elements) as MDVMetaService[];

  addedPlanList.forEach((plan) => {
    showIfExistServiceList &&
      showIfExistServiceList.forEach((service) => {
        if (
          plan.customServices &&
          !plan.customServices.find(
            (customService) =>
              customService.benefitCode &&
              customService.benefitCode.code === service.serviceBenefitCode
          ) &&
          nonExistCustomServices
        ) {
          nonExistCustomServices[service.serviceBenefitCode] =
            nonExistCustomServices[service.serviceBenefitCode]
              ? nonExistCustomServices[service.serviceBenefitCode] + 1
              : 1;
        }
      });

    showIfExistRxCostsList &&
      showIfExistRxCostsList.forEach((rxCost) => {
        if (
          (rxCost.fieldName === 'rxCost' &&
            rxCost.rxCode &&
            plan.rxCosts &&
            !(
              plan.rxCosts['inNetwork'].hasOwnProperty(rxCost.rxCode) ||
              plan.rxCosts['outOfNetwork'].hasOwnProperty(rxCost.rxCode)
            )) ||
          (rxCost.fieldName === 'mailRxCost' &&
            rxCost.rxCode &&
            plan.mailOrderRxCosts &&
            !(
              plan.mailOrderRxCosts['inNetwork'].hasOwnProperty(
                rxCost.rxCode
              ) ||
              plan.mailOrderRxCosts['outOfNetwork'].hasOwnProperty(
                rxCost.rxCode
              )
            ) &&
            nonExistRxCosts)
        ) {
          nonExistRxCosts[rxCost.fieldName + rxCost.rxCode] = nonExistRxCosts[
            rxCost.fieldName + rxCost.rxCode
          ]
            ? nonExistRxCosts[rxCost.fieldName + rxCost.rxCode] + 1
            : 1;
        }
      });

    showIfExistDeductibleIndividualList?.forEach((deductibleIndividual) => {
      if (
        deductibleIndividual.fieldName === 'indWithinFamilyDeductible' &&
        (plan?.deductibles?.hasOwnProperty(
          'individualWithinFamilyInNetworkCost'
        ) ||
          plan?.deductibles?.hasOwnProperty(
            'individualWithinFamilyOutOfNetworkCost'
          ))
      ) {
        nonExistDeductibleIndividual[deductibleIndividual.fieldName] =
          nonExistDeductibleIndividual[deductibleIndividual.fieldName]
            ? nonExistDeductibleIndividual[deductibleIndividual.fieldName] + 1
            : 1;
      }

      if (
        deductibleIndividual.fieldName === 'indWithinFamilyOOPMax' &&
        (plan?.outOfPocket?.hasOwnProperty(
          'individualWithinFamilyInNetworkCost'
        ) ||
          plan?.outOfPocket?.hasOwnProperty(
            'individualWithinFamilyOutOfNetworkCost'
          ))
      ) {
        nonExistDeductibleIndividual[deductibleIndividual.fieldName] =
          nonExistDeductibleIndividual[deductibleIndividual?.fieldName]
            ? nonExistDeductibleIndividual[deductibleIndividual?.fieldName] + 1
            : 1;
      }
    });
  });

  const excludeIfCustomServicesUnavailable = (title: MDVMetaService) => {
    const { excludeIfUnavailable } = title;
    let isExcluded = false;
    excludeIfUnavailable &&
      excludeIfUnavailable.forEach((serviceCode: number) => {
        if (serviceCode === 0 && isEditMode) {
          return (isExcluded = additionalServices.length > 0);
        }
        if (
          nonExistCustomServices &&
          nonExistCustomServices.hasOwnProperty(
            nonExistCustomServices[serviceCode]
          ) &&
          addedPlanList.length !== nonExistCustomServices[serviceCode]
        ) {
          return (isExcluded = true);
        }
      });
    return isExcluded;
  };

  const getHeaderClassName = (title: any) => {
    const baseClass = styles.sectionHeader;

    // Check for specific header field values
    if (title.fieldValue === 'DEDUCTIBLES & OOP MAXIMUM') {
      return `${baseClass} ${styles.deductiblesHeader}`;
    } else if (title.fieldValue === 'RX') {
      return `${baseClass} ${styles.rxHeader}`;
    } else if (title.fieldValue === 'SERVICES') {
      return `${baseClass} ${styles.servicesHeader}`;
    }

    // Default case
    return baseClass;
  };

  const getTextByField = (field: {
    fieldName: string;
    fieldValue?: string;
    rxCode?: string;
    fieldSpecialization?: string;
    type?: string;
  }): string => {
    let key = '';
    const isTier = field.fieldName?.includes('Cost');
    const isHeader = field.type === 'header';
    const isSpecialized = !!field.fieldSpecialization;

    if (isTier && field.rxCode) {
      key =
        RX_TIER_FIELD_KEYS?.[
          field.fieldName as keyof typeof RX_TIER_FIELD_KEYS
        ]?.[field.rxCode as keyof typeof RX_TIER_FIELD_KEYS['rxCost']];
    } else if (isSpecialized && field.fieldSpecialization) {
      key =
        SPECIALIZED_FIELD_KEYS?.[
          field.fieldName as keyof typeof SPECIALIZED_FIELD_KEYS
        ]?.[
          field.fieldSpecialization as keyof typeof SPECIALIZED_FIELD_KEYS['deductibleCost']
        ];
    } else if (isHeader) {
      key = HEADER_KEYS[field.fieldValue as keyof typeof HEADER_KEYS];
    } else {
      key = FIELD_KEYS[field.fieldName as keyof typeof FIELD_KEYS];
    }

    if (!key) {
      return field.fieldValue || '';
    }

    return getText(key, language);
  };

  const getDescriptionByField = (field: {
    fieldName: string;
    fieldValue?: string;
    rxCode?: string;
    fieldSpecialization?: string;
    type?: string;
  }): string => {
    let key = '';
    const isTier = field.fieldName?.includes('Cost');
    const isSpecialized = !!field.fieldSpecialization;

    if (isTier && field.rxCode) {
      key =
        RX_TIER_FIELD_DESCRIPTION_KEYS?.[
          field.fieldName as keyof typeof RX_TIER_FIELD_DESCRIPTION_KEYS
        ]?.[
          field.rxCode as keyof typeof RX_TIER_FIELD_DESCRIPTION_KEYS['mailRxCost']
        ];
    } else if (isSpecialized && field.fieldSpecialization) {
      key =
        SPECIALIZED_FIELD_DESCRIPTION_KEYS?.[
          field.fieldName as keyof typeof SPECIALIZED_FIELD_DESCRIPTION_KEYS
        ]?.[
          field.fieldSpecialization as keyof typeof SPECIALIZED_FIELD_DESCRIPTION_KEYS['deductibleCost']
        ];
    } else {
      key =
        FIELD_DESCRIPTION_KEYS[
          field.fieldName as keyof typeof FIELD_DESCRIPTION_KEYS
        ];
    }

    if (!key) {
      return '';
    }

    return getText(key, language);
  };

  const getCustomServiceTitle = (service: any) => {
    if (!language || language === 'ENGLISH') {
      return service.benefitCode.shortName || 'Custom Service';
    } else {
      return (
        service.benefitCode.translations?.[language]?.shortName ||
        'Custom Service'
      );
    }
  };

  const getMaxRefValue = (title: any): number | undefined => {
    if (!title || !title?.fieldName) {
      return undefined;
    }
    if (!cardValuesRefs || cardValuesRefs?.length === 0) {
      return undefined;
    }
    const refKey = fieldMapping.find((item) => {
      if (title?.fieldSpecialization) {
        return (
          item.fieldName === title.fieldName &&
          item.fieldSpecialization === title.fieldSpecialization
        );
      } else {
        return item.fieldName === title.fieldName;
      }
    })?.heightField;
    if (!refKey) {
      return undefined;
    }
    const values: number[] = [];
    cardValuesRefs.forEach((cardRef) => {
      const heightRef: MDVCardValuesRef =
        cardRef.current || ({} as MDVCardValuesRef);
      const height = heightRef[refKey];
      if (height) {
        values.push(Number(height));
      }
    });

    return values.length > 0 ? Math.max(...values) : 0;
  };

  const attachedServices = addedPlanList
    .reduce((previousValue: any, customService: any): any => {
      return [...previousValue, ...customService.customServices];
    }, [])
    .map((service: any) => service?.benefitCode?.code);

  return (
    <div
      className={joinClasses([
        styles.mdvCardTitlesWrapper,
        isEditMode ? styles.editMode : styles.previewMode,
      ])}
    >
      {isEditMode && (
        <div className={styles.addOrRemovePlansWrapper}>
          {language === 'SPANISH' ? (
            <TooltipPopover
              content="To add or remove plans, update the English version and convert it to Spanish again"
              placement="right"
            >
              <Button disabled>+ Add or Remove Plans</Button>
            </TooltipPopover>
          ) : (
            <EditableHotspot
              alwaysVisible={true}
              customModal={(visible: boolean, onClose: Function) => (
                <AddOrRemovePlans
                  visible={visible}
                  onClose={onClose}
                  title={`${planType}`}
                  onSave={onUpdatePlanList}
                  addedPlanList={addedPlanList}
                  allPlans={allPlans}
                  reloadPlans={reloadPlans}
                  channel={getMDVChannel()}
                  showAlert={showAlert}
                  isLoading={isLoading}
                />
              )}
            >
              <Button>+ Add or Remove Plans</Button>
            </EditableHotspot>
          )}
        </div>
      )}

      <div className={styles.titleContainerWrapper}>
        <div
          className={joinClasses([
            styles.titleContainer,
            styles.titleContainerMargin,
          ])}
        >
          {dataGroups.map((titleGroup, titleGroupIndex) => {
            return titleGroup.elements.map((title: any, titleIndex: number) => {
              if (!isEditMode) {
                if (notApplicableTitles.includes(title.fieldValue)) {
                  return null;
                }
              }
              if (title.availableOnly) {
                const networkType =
                  selectedNetwork === 'IN_NETWORK' ? 'IN' : 'OUT';
                if (
                  title.availableOnly &&
                  title.availableOnly !== networkType
                ) {
                  return null;
                }
              }
              if (title.type === 'header') {
                if (
                  hideHeader(
                    planType,
                    notApplicableTitles,
                    title.fieldValue,
                    selectedNetwork === 'IN_NETWORK' ? 'IN' : 'OUT'
                  )
                ) {
                  return null;
                }

                const totalWidth =
                  planColumns && planColumns.length
                    ? `${planColumns.reduce((sum, w) => sum + w, 0) + 252}px`
                    : '100%';

                return (
                  <div
                    key={`header-${titleIndex}`}
                    className={getHeaderClassName(title)}
                    style={{
                      width: totalWidth,
                    }}
                  >
                    <h3>{getTextByField(title)}</h3>
                  </div>
                );
              }

              if (
                showRxRow(title, addedPlanList, selectedNetwork) ||
                showRxRowTitle(title)
              ) {
                if (title.fieldValue?.length > 0) {
                  if (
                    (titleGroupIndex === 0 || titleGroupIndex === 1) &&
                    allowHeaderTitle
                  ) {
                    if (title.fieldName === 'monthlyCost') {
                      const contribFreq = findFrequencyByBenefitKind(
                        currentBenguide.contributionFrequencies,
                        planType.toUpperCase()
                      );
                      const { enabled } = contribFreq;
                      if (
                        !isEditMode &&
                        (!enabled ||
                          rateValidations.hasMismatchContributions ||
                          rateValidationInProgress)
                      ) {
                        return <></>;
                      }
                      return (
                        <div className={styles.tittleTopWrapper}>
                          <EditableHotspot
                            alwaysVisible={true}
                            editIcon={<img src={iconEdit} alt="edit" />}
                            customModal={getFrequencyEditor}
                            key={titleGroup.event}
                          >
                            <div
                              className={`${joinClasses([
                                styles.titleTop,
                                styles.monthlyCost,
                                planType?.toLowerCase(),
                              ])} ${
                                isEditMode &&
                                (!enabled ||
                                  rateValidations.hasMismatchContributions)
                                  ? styles.monthlyCostDisabled
                                  : ''
                              }`}
                            >
                              <span
                                className={`${styles.titleValue} ${
                                  isEditMode ? styles.titleValueTruncated : ''
                                }`}
                              >
                                {
                                  // For Spanish, place COST text before frequency: "tu costo mensual"
                                  language === 'SPANISH'
                                    ? `${getText(
                                        YOUR_COST,
                                        language
                                      )} ${getText(COST, language)} ${
                                        FREQUENCY_KEYS[
                                          contribFreq.frequency as keyof typeof FREQUENCY_KEYS
                                        ]
                                          ? getText(
                                              FREQUENCY_KEYS[
                                                contribFreq.frequency as keyof typeof FREQUENCY_KEYS
                                              ],
                                              language
                                            )
                                          : ''
                                      }`
                                    : `${getText(YOUR_COST, language)} ${
                                        FREQUENCY_KEYS[
                                          contribFreq.frequency as keyof typeof FREQUENCY_KEYS
                                        ]
                                          ? getText(
                                              FREQUENCY_KEYS[
                                                contribFreq.frequency as keyof typeof FREQUENCY_KEYS
                                              ],
                                              language
                                            )
                                          : ''
                                      } ${getText(COST, language)}`
                                }
                              </span>
                            </div>
                          </EditableHotspot>
                        </div>
                      );
                    }
                    // Hide title if it's only available for specific network
                    const networkType =
                      selectedNetwork === 'IN_NETWORK' ? 'IN' : 'OUT';
                    if (
                      title.availableOnly &&
                      title.availableOnly !== networkType
                    ) {
                      return null;
                    }
                    if (title.fieldName === 'planNetwork') {
                      if (
                        notApplicableTitles.includes('UnavailablePlanNetwork')
                      ) {
                        return (
                          <div
                            style={{
                              height: getMaxHeightWithFixedLabel(
                                title?.fieldValue
                              ),
                            }}
                            className={styles.planNetwork}
                          >
                            {' '}
                          </div>
                        );
                      }
                      return (
                        <div
                          style={{
                            height: getMaxHeightWithFixedLabel(
                              title?.fieldValue
                            ),
                          }}
                          className={styles.planNetwork}
                        >
                          <span>{getTextByField(title)}</span>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={titleIndex}
                        className={joinClasses([
                          styles.titleItem,
                          styles.planNameContainer,
                          planType === MDVTypes.MEDICAL
                            ? styles.medicalPlanName
                            : '',
                        ])}
                        style={
                          detailsHeight ? { height: detailsHeight + 4 } : {}
                        }
                      >
                        <div
                          style={{
                            height: getMaxHeightWithFixedLabel(
                              title?.fieldValue
                            ),
                          }}
                          className={
                            title.fieldValue === 'Plan Name'
                              ? joinClasses([styles.titleTop, styles.planName])
                              : joinClasses([
                                  styles.titleTop,
                                  styles.monthlyCost,
                                  planType === MDVTypes.MEDICAL
                                    ? styles.medicalCost
                                    : '',
                                ])
                          }
                        >
                          <span
                            className={`${styles.titleValue} ${
                              isEditMode ? styles.titleValueTruncated : ''
                            }`}
                          >
                            {getTextByField(title)}
                          </span>
                        </div>
                      </div>
                    );
                  }

                  if (title.fieldName === 'customFields') {
                    const uniqueCustomFields: string[] = [];

                    if (addedPlanList?.length > 0) {
                      addedPlanList?.[0]?.customFields?.forEach(
                        (customField) => {
                          uniqueCustomFields.push(customField.fieldName);
                        }
                      );
                    }

                    return (
                      <div key={titleIndex}>
                        {uniqueCustomFields
                          ?.filter(
                            (uniqueField) =>
                              !(
                                !isEditMode &&
                                notApplicableTitles?.includes(uniqueField)
                              )
                          )
                          .map((uniqueField, uniqueFieldIndex) => (
                            <SyncedLabel
                              fieldName={uniqueField}
                              className={styles.title}
                              key={uniqueFieldIndex}
                              isEditMode={isEditMode}
                            >
                              <span
                                className={`${styles.titleValue} ${
                                  isEditMode ? styles.titleValueTruncated : ''
                                }`}
                              >
                                <OverflowPopover
                                  popoverContent={uniqueField}
                                  maxWidth={'98%'}
                                  placement="right"
                                >
                                  {uniqueField}
                                </OverflowPopover>
                              </span>
                            </SyncedLabel>
                          ))}

                        {isEditMode && (
                          <div className={styles.tittleTopWrapper}>
                            <EditableHotspot
                              alwaysVisible={true}
                              customModal={getCustomRowEditor}
                            >
                              <div
                                className={joinClasses([
                                  styles.titleTop,
                                  styles.titleSeperator,
                                  planType?.toLowerCase(),
                                ])}
                              >
                                <span
                                  className={`${styles.titleValue} ${
                                    isEditMode ? styles.titleValueTruncated : ''
                                  }`}
                                >
                                  + Manage Custom Rows
                                </span>
                              </div>
                            </EditableHotspot>
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    if (
                      title.alwaysShowTitle ||
                      (!title.serviceBenefitCode &&
                        !title.rxCode &&
                        !title.additionalServices) ||
                      (title.serviceBenefitCode &&
                        // Only check for advanced profile if this is a service with showTitleIfExists=true
                        (!title.showTitleIfExists ||
                          currentBenguide.isAdvanceProfile) &&
                        (!nonExistCustomServices.hasOwnProperty(
                          title.serviceBenefitCode
                        ) ||
                          addedPlanList.length !==
                            nonExistCustomServices[
                              title.serviceBenefitCode
                            ])) ||
                      (title.rxCode &&
                        (isEditMode ||
                          !nonExistRxCosts.hasOwnProperty(
                            title.fieldName + title.rxCode
                          ) ||
                          addedPlanList.length !==
                            nonExistRxCosts[title.fieldName + title.rxCode]))
                    ) {
                      let tooltipContent;
                      if (typeof title.fieldValue === 'string') {
                        tooltipContent = TOOLTIP_CONST[title.fieldValue];
                      }
                      // Does not render mental healh in patient and outpatient
                      const baseTitle = getTextByField(title);
                      // For services and RX, use the service title for height sync
                      // For other fields, use the fieldValue
                      const heightSyncKey =
                        title.serviceBenefitCode || title.rxCode
                          ? baseTitle
                          : title?.fieldValue;
                      const height = getMaxRefValue(title);

                      return (
                        <div
                          key={title.fieldValue}
                          className={styles.titleItem}
                        >
                          <SyncedLabel
                            fieldName={heightSyncKey}
                            className={styles.title}
                            isEditMode={isEditMode}
                            adjustedHeight={height}
                          >
                            {isEditMode ||
                            isEmpty(getDescriptionByField(title)) ||
                            isNullOrUndefined(getDescriptionByField(title)) ? (
                              <div
                                className={`${styles.titleValue} ${
                                  isEditMode ? styles.titleValueTruncated : ''
                                }`}
                              >
                                <div>
                                  <OverflowPopover placement="right">
                                    {baseTitle}
                                  </OverflowPopover>
                                </div>
                                <div>
                                  {tooltipContent && (
                                    <PlanyearPopover
                                      content={
                                        <div>{tooltipContent.subtitle}</div>
                                      }
                                      placement="right"
                                    >
                                      <QuestionCircleOutlined
                                        className={styles.flexicon}
                                      />
                                    </PlanyearPopover>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div
                                className={`${styles.titleValue} ${
                                  isEditMode ? styles.titleValueTruncated : ''
                                }`}
                              >
                                {Object.keys(TOOLTIP_CONST).includes(
                                  baseTitle
                                ) ? (
                                  <div>{baseTitle}</div>
                                ) : (
                                  <TooltipPopover
                                    content={getDescriptionByField(title)}
                                    placement="right"
                                  >
                                    <div>{baseTitle}</div>
                                  </TooltipPopover>
                                )}
                                <div>
                                  {tooltipContent && (
                                    <PlanyearPopover
                                      content={
                                        <div>{tooltipContent.subtitle}</div>
                                      }
                                      placement="right"
                                    >
                                      <QuestionCircleOutlined
                                        className={styles.flexicon}
                                      />
                                    </PlanyearPopover>
                                  )}
                                </div>
                              </div>
                            )}
                          </SyncedLabel>
                        </div>
                      );
                    } else {
                      return null;
                    }
                  }
                }
                if (title.fieldName === 'spaceField') {
                  if (title.includeIfServiceAvailable) {
                    const serviceAvailable = attachedServices.some((r: any) =>
                      title.includeIfServiceAvailable.includes(r)
                    );
                    return serviceAvailable ? (
                      <div className={styles.titleSeperator}></div>
                    ) : null;
                  } else if (
                    title?.isCustomField &&
                    isEditMode &&
                    addedPlanList?.[0]?.customFields?.length === 0
                  ) {
                    return <div className={styles.titleSeperator}></div>;
                  } else if (title.isCustomField && isEditMode) {
                    return null;
                  }
                }
                if (isEditMode && title.isRx) {
                  return <></>;
                }

                if (
                  !title.additionalServices &&
                  (!title.excludeIfUnavailable ||
                    (title.excludeIfUnavailable &&
                      excludeIfCustomServicesUnavailable(title)))
                )
                  return (
                    <div key={titleIndex} className={styles.titleSeperator} />
                  );

                if (title.additionalServices) {
                  if (isEditMode) {
                    const metadataEntries = dataGroups
                      .flatMap((group) => group.elements)
                      .filter((item: any) => item.serviceBenefitCode)
                      .map((item: any) => ({
                        code: item.serviceBenefitCode,
                        showTitleIfExists: item.showTitleIfExists === true,
                      }));

                    const metadataServiceCodes = metadataEntries.map(
                      (entry) => entry.code
                    );

                    const customServiceTitles: any[] = [];

                    addedPlanList.forEach((plan) => {
                      (plan.customServices || []).forEach((service) => {
                        if (
                          service.benefitCode?.benefitKind ===
                            planType.toUpperCase() &&
                          service.benefitCode?.code !== undefined &&
                          !service.benefitCode.isDefault
                        ) {
                          const serviceCode = service.benefitCode.code;
                          const isInMetadata =
                            metadataServiceCodes.includes(serviceCode);

                          if (
                            !isInMetadata &&
                            !customServiceTitles.some(
                              (item) => item.code === serviceCode
                            )
                          ) {
                            customServiceTitles.push({
                              code: serviceCode,
                              title: getCustomServiceTitle(service),
                            });
                          }
                        }
                      });
                    });
                    return (
                      <>
                        {customServiceTitles.map((customService, index) => (
                          <div
                            key={`custom-title-${index}`}
                            className={styles.titleItem}
                          >
                            <SyncedLabel
                              fieldName={customService.title}
                              className={styles.title}
                              isEditMode={isEditMode}
                            >
                              <div
                                className={`${styles.titleValue} ${
                                  isEditMode ? styles.titleValueTruncated : ''
                                }`}
                              >
                                <div>
                                  <OverflowPopover placement="right">
                                    {customService.title}
                                  </OverflowPopover>
                                </div>
                              </div>
                            </SyncedLabel>
                          </div>
                        ))}
                      </>
                    );
                  } else {
                    const metadataEntries = dataGroups
                      .flatMap((group) => group.elements)
                      .filter((item: any) => item.serviceBenefitCode)
                      .map((item: any) => ({
                        code: item.serviceBenefitCode,
                        showTitleIfExists: item.showTitleIfExists === true,
                      }));

                    const metadataServiceCodes = metadataEntries.map(
                      (entry) => entry.code
                    );

                    const customServiceTitles: any[] = [];

                    addedPlanList.forEach((plan) => {
                      (plan.customServices || []).forEach((service) => {
                        const networkValue =
                          service.serviceValue?.[
                            selectedNetwork === 'IN_NETWORK'
                              ? 'inNetwork'
                              : 'outOfNetwork'
                          ];

                        const info = networkValue?.info;
                        const copay = networkValue?.copay;
                        const coinsurance = networkValue?.coinsurance;

                        const hasOnlyValidInfo =
                          info !== undefined &&
                          copay === undefined &&
                          coinsurance === undefined &&
                          !isInvalidDisplayValue(info);

                        const hasValidInfoWithCopayOrCoinsurance =
                          info !== undefined &&
                          (copay !== undefined || coinsurance !== undefined) &&
                          (!isInvalidDisplayValue(copay) ||
                            !isInvalidDisplayValue(coinsurance));

                        if (
                          service.benefitCode?.benefitKind ===
                            planType.toUpperCase() &&
                          service.benefitCode?.code !== undefined &&
                          !service.benefitCode.isDefault &&
                          (hasOnlyValidInfo ||
                            hasValidInfoWithCopayOrCoinsurance)
                        ) {
                          const serviceCode = service.benefitCode.code;
                          const isInMetadata =
                            metadataServiceCodes.includes(serviceCode);

                          if (
                            !isInMetadata &&
                            !customServiceTitles.some(
                              (item) => item.code === serviceCode
                            )
                          ) {
                            customServiceTitles.push({
                              code: serviceCode,
                              title: getCustomServiceTitle(service),
                            });
                          }
                        }
                      });
                    });

                    return (
                      <>
                        {customServiceTitles.map((customService, index) => (
                          <div
                            key={`custom-title-${index}`}
                            className={styles.titleItem}
                          >
                            <SyncedLabel
                              fieldName={customService.title}
                              className={styles.title}
                              isEditMode={isEditMode}
                            >
                              <div
                                className={`${styles.titleValue} ${
                                  isEditMode ? styles.titleValueTruncated : ''
                                }`}
                              >
                                <div>
                                  <OverflowPopover placement="right">
                                    {customService.title}
                                  </OverflowPopover>
                                </div>
                              </div>
                            </SyncedLabel>
                          </div>
                        ))}
                      </>
                    );
                  }
                }
                return null;
              }
            });
          })}
        </div>
      </div>
    </div>
  );
};
